import { Grid } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import ClosedQuestionsContainer from './closed-questions-container';
import DemographicQuestionsContainer from './demographic-questions-container';
import EffectQuestionsContainer from './effect-questions-container';
import OpenQuestionsContainer from './open-questions-container';
import CustomLinearProgress from './custom-linear-progress';
import { Answer, ClosedQuestion, Demographics, EffectQuestion, OpenQuestion, Perspectives } from './lead-questionnaire.types';

interface Props {
  categoryIndex: number;
  perspective?: Perspectives;
  closedQuestions: ClosedQuestion[];
  openQuestions: OpenQuestion[];
  effectQuestions: EffectQuestion[];
  closedAnswers: Answer[];
  effectAnswers: Answer[];
  openAnswers: Answer[];
  demographicAnswers: Demographics;
  sendAnswersLoading: boolean;

  handleClosedRadioSelectionChange(id: string): (value: string | number) => () => void;

  handleEffectRadioSelectionChange(id: string): (value: string | number) => () => void;

  handleOpenTextFieldChange(id: string): (e: React.ChangeEvent<HTMLInputElement>) => void;

  nextCategory(): void;

  previousCategory(): void;

  scrollToTop(): void;

  sendAnswers(): void;

  //  demographic handlers

  handleBranchCheckboxChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleCompanySizeRadioChange(value: string): () => void;

  handleTeamSizeRadioChange(value: string): () => void;

  handleManagementFieldRadioChange(value: string): () => void;

  handleTeamTypeRadioChange(value: string): () => void;

  handleCustomerResponsibilityRadioChange(value: string): () => void;

  handleResponsibilityFieldRadioChange(value: string): () => void;
}

const Questions: React.FC<Props> = ({
  categoryIndex,
  perspective,
  closedQuestions,
  openQuestions,
  effectQuestions,
  closedAnswers,
  effectAnswers,
  openAnswers,
  demographicAnswers,
  sendAnswersLoading,
  handleClosedRadioSelectionChange,
  handleEffectRadioSelectionChange,
  handleOpenTextFieldChange,
  nextCategory,
  previousCategory,
  scrollToTop,
  sendAnswers,
  handleBranchCheckboxChange,
  handleCompanySizeRadioChange,
  handleTeamSizeRadioChange,
  handleManagementFieldRadioChange,
  handleTeamTypeRadioChange,
  handleCustomerResponsibilityRadioChange,
  handleResponsibilityFieldRadioChange,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const leadLogo = '/dashboard/images/lead/lead_logo.png';

  const [allSelected, setAllSelected] = useState(false);
  const [showClosedQuestions] = useState<boolean>(true);
  const [showOpenQuestions, setShowOpenQuestions] = useState<boolean>(false);
  const [showEffectQuestions, setShowEffectQuestions] = useState<boolean>(false);
  const [showDemographicQuestions, setShowDemographicQuestions] = useState<boolean>(false);

  const showEffect = () => {
    if (effectQuestions.length > 0) {
      setShowEffectQuestions(true);
      scrollToTop();
    }
  };

  const hideEffect = () => {
    if (effectQuestions.length > 0) {
      setShowEffectQuestions(false);
      scrollToTop();
    }
    setAllSelected(true);
  };

  const showOpen = () => {
    if (openQuestions.length > 0) {
      setShowOpenQuestions(true);
      scrollToTop();
    }
  };

  const hideOpen = () => {
    if (openQuestions.length > 0) {
      setShowOpenQuestions(false);
      scrollToTop();
    }
    setAllSelected(true);
  };

  const showDemographic = () => {
    setShowDemographicQuestions(true);
    scrollToTop();
  };

  const hideDemographic = () => {
    setShowDemographicQuestions(false);
    scrollToTop();
    setAllSelected(true);
  };

  useEffect(() => {
    const questionsIds: any[] = [];
    closedQuestions[categoryIndex].subCategories.forEach((subCategory) =>
      subCategory.questions.forEach((question) => questionsIds.push(question.id))
    );
    const answerIds = _.map(closedAnswers, (answer) => {
      return answer.id;
    });
    const intersectingIds = _.intersection(questionsIds, answerIds);
    if (_.includes([Perspectives.COLLEAGUE, Perspectives.BOSS, Perspectives.OTHER], perspective) || _.isEqual(questionsIds, intersectingIds)) {
      setAllSelected(true);
    } else {
      setAllSelected(false);
    }
  }, [closedAnswers, categoryIndex]);

  useEffect(() => {
    if (showEffectQuestions) {
      const questionsIds: any[] = [];
      effectQuestions.forEach((subCategory) => subCategory.questions.forEach((question) => questionsIds.push(question.id)));
      const answerIds = _.map(effectAnswers, (answer) => {
        return answer.id;
      });
      const intersectingIds = _.intersection(questionsIds, answerIds);
      if (_.includes([Perspectives.COLLEAGUE, Perspectives.BOSS, Perspectives.OTHER], perspective) || _.isEqual(questionsIds, intersectingIds)) {
        setAllSelected(true);
      } else {
        setAllSelected(false);
      }
    }
  }, [effectAnswers, showEffectQuestions]);

  const renderQuestions = () => {
    if (showDemographicQuestions) {
      return (
        <DemographicQuestionsContainer
          demographicAnswers={demographicAnswers}
          handleBranchCheckboxChange={handleBranchCheckboxChange}
          handleCompanySizeRadioChange={handleCompanySizeRadioChange}
          handleTeamSizeRadioChange={handleTeamSizeRadioChange}
          handleManagementFieldRadioChange={handleManagementFieldRadioChange}
          handleTeamTypeRadioChange={handleTeamTypeRadioChange}
          handleCustomerResponsibilityRadioChange={handleCustomerResponsibilityRadioChange}
          handleResponsibilityFieldRadioChange={handleResponsibilityFieldRadioChange}
        />
      );
    } else if (showOpenQuestions) {
      return (
        <OpenQuestionsContainer
          perspective={perspective}
          openQuestions={openQuestions}
          answers={openAnswers}
          handleOpenTextFieldChange={handleOpenTextFieldChange}
        />
      );
    } else if (showEffectQuestions) {
      return (
        <EffectQuestionsContainer
          perspective={perspective}
          effectQuestions={effectQuestions}
          answers={effectAnswers}
          handleRadioSelectionChange={handleEffectRadioSelectionChange}
        />
      );
    } else if (showClosedQuestions) {
      return (
        <ClosedQuestionsContainer
          perspective={perspective}
          closedQuestion={closedQuestions[categoryIndex]}
          answers={closedAnswers}
          handleRadioSelectionChange={handleClosedRadioSelectionChange}
        />
      );
    } else {
      return <div>something went wrong!</div>;
    }
  };

  return (
    <Grid container={true} spacing={2} justifyContent={'flex-end'}>
      <Grid className={classes.progressBarContainer} size={12}>
        <img className={classes.logo} src={leadLogo} alt={'Lead Logo'} />
        <div style={{ width: 'calc(100% - 250px)', marginLeft: '32px' }}>
          <CustomLinearProgress
            value={
              ((categoryIndex +
                (showEffectQuestions ? 1 : 0) +
                (showOpenQuestions ? 1 : 0) +
                (showDemographicQuestions && perspective === Perspectives.SELF ? 1 : 0)) /
                (closedQuestions.length +
                  (effectQuestions.length > 0 ? 1 : 0) +
                  (openQuestions.length > 0 ? 1 : 0) +
                  (perspective === Perspectives.SELF ? 1 : 0))) *
              100
            }
          />
        </div>
      </Grid>
      <Grid size={12}>
        {renderQuestions()}
      </Grid>
      <Grid>
        {!showEffectQuestions && !showOpenQuestions && !showDemographicQuestions && categoryIndex < closedQuestions.length && categoryIndex !== 0 ? (
          <PaiButton text={t('back')} size={'large'} action={previousCategory} />
        ) : !showOpenQuestions && !showDemographicQuestions && showEffectQuestions && effectQuestions.length > 0 ? (
          <PaiButton text={t('back')} size={'large'} action={hideEffect} />
        ) : !showDemographicQuestions && showOpenQuestions && openQuestions.length > 0 ? (
          <PaiButton text={t('back')} size={'large'} action={hideOpen} />
        ) : showDemographicQuestions ? (
          <PaiButton text={t('back')} size={'large'} action={hideDemographic} />
        ) : undefined}
      </Grid>
      <Grid>
        {categoryIndex + 1 < closedQuestions.length ? (
          <PaiButton text={t('continue')} size={'large'} action={nextCategory} disabled={!allSelected} />
        ) : !showEffectQuestions && effectQuestions.length > 0 ? (
          <PaiButton text={t('continue')} size={'large'} action={showEffect} disabled={!allSelected} />
        ) : !showOpenQuestions && openQuestions.length > 0 ? (
          <PaiButton text={t('continue')} size={'large'} action={showOpen} disabled={!allSelected} />
        ) : !showDemographicQuestions && perspective === 'self' ? (
          <PaiButton text={t('continue')} size={'large'} action={showDemographic} disabled={!allSelected} />
        ) : (
          <PaiButton
            text={t('leadQuestionnaireSendSurvey')}
            size={'large'}
            action={sendAnswers}
            disabled={sendAnswersLoading}
            loading={sendAnswersLoading}
          />
        )}
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles()(() => ({
  progressBarContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    paddingBottom: '16px',
  },
  progressBar: {
    height: '20px',
    border: '1px solid',
    backgroundColor: '#fff',
    marginLeft: '24px',
  },
  logo: {
    width: '250px',
  },
}));

export default Questions;
